import React, { useState } from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ContactPage.module.css';

const IMG = '/static/images/contact';

const layoutAreas = `
  topbar
  main
  footer
`;

// ---------- Schema ----------

function buildPageSchema(rootURL) {
  return {
    '@type': 'ContactPage',
    name: 'Contact Avento | Get in touch with us',
    url: `${rootURL}/contact`,
    description:
      'Have questions about skydiving gear or our marketplace? Get in touch with Avento through our contact form, or reach us directly by phone or email.',
    inLanguage: 'en',
    mainEntity: {
      '@type': 'Organization',
      '@id': `${rootURL}#organization`,
      name: 'Avento Gear GmbH',
      url: rootURL,
      areaServed: {
        '@type': 'Place',
        name: 'Worldwide',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Unterstrasse 6',
        addressLocality: 'St. Gallen',
        addressRegion: 'SG',
        postalCode: '9000',
        addressCountry: 'CH',
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'lean@aventogear.com',
          telephone: '+41 79 943 44 99',
          availableLanguage: ['English', 'German'],
          contactOption: ['Email', 'Phone', 'ContactForm'],
          areaServed: 'Worldwide',
        },
      ],
    },
  };
}

// ---------- Contact Form ----------

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  message: '',
  website: '', // honeypot
};

const ContactForm = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.website) return;

    setStatus('submitting');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className={css.formBlock}>
      <form
        id="wf-form-Contact-Form"
        name="wf-form-Contact-Form"
        className={css.contactForm}
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 className={css.formHeading}>Contact Us</h2>

        <div className={css.formGrid}>
          <div className={css.formGridCard}>
            <label htmlFor="First-Name" className={css.formLabel}>
              First Name
            </label>
            <input
              id="First-Name"
              name="First-Name"
              type="text"
              maxLength={256}
              className={css.formFieldSmall}
              value={form.firstName}
              onChange={e =>
                setForm(prev => ({ ...prev, firstName: e.target.value }))
              }
              autoComplete="given-name"
            />
          </div>
          <div className={css.formGridCard}>
            <label htmlFor="Last-Name" className={css.formLabel}>
              Last Name
            </label>
            <input
              id="Last-Name"
              name="Last-Name"
              type="text"
              maxLength={256}
              className={css.formFieldSmall}
              value={form.lastName}
              onChange={e =>
                setForm(prev => ({ ...prev, lastName: e.target.value }))
              }
              autoComplete="family-name"
            />
          </div>
        </div>

        <label htmlFor="email" className={css.formLabelMandatory}>
          Email Address*
        </label>
        <input
          id="email"
          name="email"
          type="email"
          maxLength={256}
          required
          className={css.formFieldSmall}
          value={form.email}
          onChange={e =>
            setForm(prev => ({ ...prev, email: e.target.value }))
          }
          autoComplete="email"
        />

        <label htmlFor="Message" className={css.formLabelMandatory}>
          Message*
        </label>
        <textarea
          id="Message"
          name="Message"
          maxLength={5000}
          required
          className={css.formFieldLarge}
          value={form.message}
          onChange={e =>
            setForm(prev => ({ ...prev, message: e.target.value }))
          }
        />

        {/* Honeypot */}
        <div className={css.honeypot} aria-hidden="true">
          <label htmlFor="contact-website">Website</label>
          <input
            id="contact-website"
            type="text"
            name="website"
            value={form.website}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <button
          type="submit"
          className={css.buttonPrimary}
          disabled={status === 'submitting'}
        >
          {status === 'submitting' ? 'Sending...' : 'Send'}
        </button>
      </form>

      {status === 'success' && (
        <div className={css.successMessage}>
          <div className={css.formSuccessWrapper}>
            <img
              src={`${IMG}/icon-check.png`}
              alt=""
              className={css.formSuccessIcon}
              width={50}
              height={50}
            />
            <h2 className={css.formSuccessHeading}>Thank You!</h2>
            <p className={css.formSuccessText}>
              Your Message has been Submitted. You will hear from us shortly!
            </p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className={css.formErrorCard}>
          Something went wrong. Try again or contact us directly via Mail or
          WhatsApp.
        </div>
      )}
    </div>
  );
};

// ---------- Main component ----------

const ContactPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const rootURL = config.marketplaceRootURL || '';
  const schemaTitle = 'Contact Avento | Get in touch with us';
  const schemaDescription =
    'Questions about a listing, payment, or did you encounter a problem? Reach us by email, phone or contact form for questions about buying or selling skydiving gear.';
  const pageSchema = buildPageSchema(rootURL);

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={pageSchema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="ContactPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                {/* Hero (protection-hero) */}
                <section className={css.hero}>
                  <div className={css.heroInner}>
                    <div className={css.heroGrid}>
                      <div className={css.heroTextWrap}>
                        <h1 className={css.heroTitle}>Get in Touch!</h1>
                        <p className={css.heroBody}>
                          Do you have any questions or feedback? Please fill out
                          the contact form, and we will come back to you shortly
                          (usually within 24 hours). We are looking forward to
                          hearing from you!
                        </p>
                      </div>
                      <img
                        src={`${IMG}/contact-hero.webp`}
                        alt="Avento founders Lean and Tim"
                        className={css.heroImage}
                        width={1200}
                        height={800}
                        loading="eager"
                      />
                    </div>
                  </div>
                </section>

                {/* Form + Details */}
                <section className={css.section}>
                  <div className={css.container}>
                    <div className={css.contactWrapperGrid}>
                      <ContactForm />

                      <div className={css.contactDetailsWrapper}>
                        <h2 className={css.contactDetailsHeading}>Details</h2>
                        <div className={css.contactDetailsText}>
                          <strong>Avento Gear GmbH</strong>
                        </div>
                        <div className={css.contactDetailsText}>
                          Unterstrasse 6, St. Gallen, SG, 9000, CH
                        </div>
                        <div className={css.contactDetailsText}>
                          <a
                            href="mailto:lean@aventogear.com?subject=Inquiry%20from%20Avento"
                            className={css.contactDetailsLink}
                          >
                            lean@aventogear.com
                            <br />
                            +41 79 943 44 99
                          </a>
                        </div>
                        <div className={css.contactDetailsText}>
                          SHAB: 1006490163
                          <br />
                          UID: CHE-185.376.046
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* CTA Cards */}
                <section className={`${css.section} ${css.lastSection}`}>
                  <div className={css.container}>
                    <div className={css.contactCtaGrid}>
                      <div className={css.contactCtaCard}>
                        <div className={css.contactCtaLearn}>
                          <img
                            src={`${IMG}/icon-question.png`}
                            alt=""
                            className={css.contactIcon}
                            width={50}
                            height={50}
                          />
                          <h2 className={css.contactCtaHeading}>Learn More</h2>
                          <div className={css.contactCtaText}>
                            Check the most commonly asked questions
                          </div>
                        </div>
                        <a href="/faq" className={css.buttonOutlined}>
                          See FAQ
                        </a>
                      </div>

                      <div className={css.contactCtaCard}>
                        <img
                          src={`${IMG}/icon-whatsapp.png`}
                          alt=""
                          className={css.contactIcon}
                          width={50}
                          height={50}
                        />
                        <h2 className={css.contactCtaHeading}>Stay in Touch</h2>
                        <div className={css.contactCtaText}>
                          Join our WhatsApp channel to participate and get
                          exclusive updates
                        </div>
                        <a
                          href="https://whatsapp.com/channel/0029Vb694ou2f3ELVP7QrK0T"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={css.buttonPrimary}
                        >
                          Join here
                        </a>
                      </div>
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const ContactPage = connect(mapStateToProps)(ContactPageComponent);

export default ContactPage;
